import React, { useState, useEffect, useCallback } from 'react';
import { ClipboardCheck, Hammer, Lightbulb, Plus, X, Check, Trash2, Loader, ArrowRightLeft, ChevronUp, ChevronDown, Star, Pencil } from 'lucide-react';
import { neonQuery } from '../services/api';

const tabConfig = [
  { key: 'testing', label: 'Needs Testing', icon: ClipboardCheck, color: 'var(--orange)', bgColor: '#fff3ed', userEditable: false },
  { key: 'current', label: 'Current Build', icon: Hammer, color: 'var(--navy)', bgColor: '#e8f0fe', userEditable: true },
  { key: 'backlog', label: 'Backlog', icon: Lightbulb, color: 'var(--green)', bgColor: '#e6f4ea', userEditable: true },
];

export default function TodoBoard() {
  const [items, setItems] = useState([]);
  const [activeTab, setActiveTab] = useState('testing');
  const [newItem, setNewItem] = useState('');
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');

  const loadItems = useCallback(async () => {
    try {
      const rows = await neonQuery('todo_list');
      setItems(rows);
    } catch (e) {
      // Table may not exist yet — init it
      try {
        const rows = await neonQuery('todo_init');
        setItems(rows);
      } catch (e2) {
        console.error('Todo load error:', e2);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadItems(); }, [loadItems]);

  const activeConfig = tabConfig.find(t => t.key === activeTab);
  const tabItems = items.filter(i => i.tab === activeTab);

  const addItem = useCallback(async () => {
    const text = newItem.trim();
    if (!text) return;
    setNewItem('');
    try {
      const rows = await neonQuery('todo_add', { tab: activeTab, text });
      setItems(rows);
    } catch (e) {
      console.error('Todo add error:', e);
    }
  }, [newItem, activeTab]);

  const removeItem = useCallback(async (id) => {
    try {
      const rows = await neonQuery('todo_remove', { id });
      setItems(rows);
    } catch (e) {
      console.error('Todo remove error:', e);
    }
  }, []);

  const moveItem = useCallback(async (id, toTab) => {
    try {
      const rows = await neonQuery('todo_move', { id, tab: toTab });
      setItems(rows);
    } catch (e) {
      console.error('Todo move error:', e);
    }
  }, []);

  const reorderItem = useCallback(async (id, direction) => {
    try {
      const rows = await neonQuery('todo_reorder', { id, direction });
      setItems(rows);
    } catch (e) {
      console.error('Todo reorder error:', e);
    }
  }, []);

  const toggleHighlight = useCallback(async (id) => {
    try {
      const rows = await neonQuery('todo_highlight', { id });
      setItems(rows);
    } catch (e) {
      console.error('Todo highlight error:', e);
    }
  }, []);

  const startEdit = (item) => {
    setEditingId(item.id);
    setEditText(item.text);
  };

  const saveEdit = useCallback(async () => {
    if (!editingId || !editText.trim()) return;
    try {
      const rows = await neonQuery('todo_edit', { id: editingId, text: editText.trim() });
      setItems(rows);
    } catch (e) {
      console.error('Todo edit error:', e);
    }
    setEditingId(null);
    setEditText('');
  }, [editingId, editText]);

  const clearAll = useCallback(async (tab) => {
    try {
      const rows = await neonQuery('todo_clear', { tab });
      setItems(rows);
    } catch (e) {
      console.error('Todo clear error:', e);
    }
  }, []);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') addItem();
  };

  if (loading) {
    return (
      <div style={{ padding: 24, display: 'flex', alignItems: 'center', gap: 8, color: 'var(--gray-500)' }}>
        <Loader size={16} className="spin" /> Loading To-Do board...
      </div>
    );
  }

  return (
    <div className="todo-page" style={{ padding: 24, maxWidth: 800 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <ClipboardCheck size={24} color="var(--orange)" />
        <h1 style={{ fontSize: 20, fontWeight: 700, color: 'var(--navy)', margin: 0 }}>To-Do Board</h1>
      </div>

      {/* Tabs */}
      <div className="todo-tabs" style={{ display: 'flex', gap: 0, marginBottom: 0, borderBottom: '2px solid var(--gray-200)' }}>
        {tabConfig.map(tab => {
          const Icon = tab.icon;
          const count = items.filter(i => i.tab === tab.key).length;
          const isActive = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '10px 20px',
                border: 'none',
                borderBottom: isActive ? `3px solid ${tab.color}` : '3px solid transparent',
                background: isActive ? tab.bgColor : 'transparent',
                color: isActive ? tab.color : 'var(--gray-700)',
                fontWeight: isActive ? 700 : 500,
                fontSize: 12.5,
                cursor: 'pointer',
                fontFamily: 'inherit',
                transition: 'all 0.15s',
                marginBottom: -2,
                borderRadius: '6px 6px 0 0',
              }}
            >
              <Icon size={14} />
              {tab.label}
              {count > 0 && (
                <span style={{
                  background: tab.color, color: '#fff',
                  fontSize: 10, fontWeight: 700,
                  borderRadius: 10, padding: '1px 7px',
                  marginLeft: 2,
                }}>{count}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      <div style={{
        background: 'var(--white)',
        border: '1px solid var(--gray-200)',
        borderTop: 'none',
        borderRadius: '0 0 8px 8px',
        minHeight: 300,
        padding: 20,
      }}>
        {/* Info banner */}
        {activeTab === 'testing' && (
          <div style={{
            background: '#fff3ed',
            border: '1px solid #fcd5c0',
            borderRadius: 6,
            padding: '10px 14px',
            fontSize: 11.5,
            color: 'var(--gray-700)',
            marginBottom: 16,
            lineHeight: 1.5,
          }}>
            Items appear here automatically when Claude builds a feature. Tell Claude it's tested/done/perfect to remove it.
          </div>
        )}

        {activeTab === 'current' && (
          <div style={{
            background: '#e8f0fe',
            border: '1px solid #c5d7f2',
            borderRadius: 6,
            padding: '10px 14px',
            fontSize: 11.5,
            color: 'var(--gray-700)',
            marginBottom: 16,
            lineHeight: 1.5,
          }}>
            Features currently being built. Add items here to track what's in progress.
          </div>
        )}

        {activeTab === 'backlog' && (
          <div style={{
            background: '#e6f4ea',
            border: '1px solid #b7e1c4',
            borderRadius: 6,
            padding: '10px 14px',
            fontSize: 11.5,
            color: 'var(--gray-700)',
            marginBottom: 16,
            lineHeight: 1.5,
          }}>
            Ideas and future features. Capture them here so nothing gets lost.
          </div>
        )}

        {/* Input area for current/backlog */}
        {activeConfig.userEditable && (
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            <input
              type="text"
              value={newItem}
              onChange={(e) => setNewItem(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={activeTab === 'current' ? 'Add a feature to build...' : 'Add a backlog idea...'}
              style={{
                flex: 1,
                padding: '8px 12px',
                border: '1px solid var(--gray-300)',
                borderRadius: 6,
                fontSize: 12.5,
                fontFamily: 'inherit',
                outline: 'none',
              }}
            />
            <button
              onClick={addItem}
              disabled={!newItem.trim()}
              style={{
                display: 'flex', alignItems: 'center', gap: 4,
                padding: '8px 14px',
                background: newItem.trim() ? activeConfig.color : 'var(--gray-300)',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                fontSize: 12,
                fontWeight: 600,
                cursor: newItem.trim() ? 'pointer' : 'default',
                fontFamily: 'inherit',
                transition: 'all 0.15s',
              }}
            >
              <Plus size={14} /> Add
            </button>
          </div>
        )}

        {/* Items list */}
        {tabItems.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '40px 20px',
            color: 'var(--gray-500)',
            fontSize: 12.5,
          }}>
            {activeTab === 'testing'
              ? 'Nothing to test right now — all caught up!'
              : activeTab === 'current'
              ? 'No active features — add something to build!'
              : 'No backlog items — add ideas as they come!'}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {tabItems.map((item, idx) => (
              <div
                key={item.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '10px 12px',
                  background: item.highlighted ? '#fffde7' : 'var(--gray-50)',
                  borderRadius: 6,
                  border: item.highlighted ? '1px solid #f9e547' : '1px solid var(--gray-200)',
                  fontSize: 12.5,
                  transition: 'all 0.15s',
                }}
              >
                {/* Reorder arrows for current/backlog */}
                {(activeTab === 'current' || activeTab === 'backlog') && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 0, flexShrink: 0 }}>
                    <button onClick={() => reorderItem(item.id, 'up')} disabled={idx === 0}
                      style={{ background: 'none', border: 'none', cursor: idx === 0 ? 'default' : 'pointer', color: idx === 0 ? 'var(--gray-300)' : 'var(--gray-500)', padding: 0, display: 'flex', lineHeight: 1 }}>
                      <ChevronUp size={14} />
                    </button>
                    <button onClick={() => reorderItem(item.id, 'down')} disabled={idx === tabItems.length - 1}
                      style={{ background: 'none', border: 'none', cursor: idx === tabItems.length - 1 ? 'default' : 'pointer', color: idx === tabItems.length - 1 ? 'var(--gray-300)' : 'var(--gray-500)', padding: 0, display: 'flex', lineHeight: 1 }}>
                      <ChevronDown size={14} />
                    </button>
                  </div>
                )}
                <div style={{
                  width: 6, height: 6,
                  borderRadius: '50%',
                  background: activeConfig.color,
                  flexShrink: 0,
                }} />
                {editingId === item.id ? (
                  <input
                    autoFocus
                    value={editText}
                    onChange={e => setEditText(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') { setEditingId(null); setEditText(''); } }}
                    onBlur={saveEdit}
                    style={{ flex: 1, fontSize: 12.5, fontFamily: 'inherit', padding: '4px 8px', border: '1px solid var(--gray-300)', borderRadius: 4, outline: 'none' }}
                  />
                ) : (
                  <span style={{ flex: 1, color: 'var(--gray-900)', fontWeight: item.highlighted ? 700 : 400 }}>{item.text}</span>
                )}
                <span style={{ fontSize: 10, color: 'var(--gray-500)', flexShrink: 0 }}>
                  {new Date(item.added_at).toLocaleDateString()}
                </span>
                {/* Edit button for current/backlog */}
                {(activeTab === 'current' || activeTab === 'backlog') && editingId !== item.id && (
                  <button
                    onClick={() => startEdit(item)}
                    title="Edit"
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer',
                      color: 'var(--gray-400)', padding: 2, display: 'flex',
                      alignItems: 'center', transition: 'all 0.15s',
                    }}
                  >
                    <Pencil size={12} />
                  </button>
                )}
                {/* Highlight toggle for current/backlog */}
                {(activeTab === 'current' || activeTab === 'backlog') && (
                  <button
                    onClick={() => toggleHighlight(item.id)}
                    title={item.highlighted ? 'Remove highlight' : 'Highlight'}
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer',
                      color: item.highlighted ? '#f5a623' : 'var(--gray-400)',
                      padding: 2, display: 'flex', alignItems: 'center',
                      transition: 'all 0.15s',
                    }}
                  >
                    <Star size={13} fill={item.highlighted ? '#f5a623' : 'none'} />
                  </button>
                )}
                {(activeTab === 'current' || activeTab === 'backlog') && (
                  <button
                    onClick={() => moveItem(item.id, activeTab === 'current' ? 'backlog' : 'current')}
                    title={activeTab === 'current' ? 'Move to Backlog' : 'Move to Current Build'}
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer',
                      color: 'var(--gray-500)', padding: 2, display: 'flex',
                      alignItems: 'center', transition: 'all 0.15s',
                    }}
                  >
                    <ArrowRightLeft size={13} />
                  </button>
                )}
                <button
                  onClick={() => removeItem(item.id)}
                  title={activeTab === 'testing' ? 'Mark as tested' : 'Remove'}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: activeTab === 'testing' ? 'var(--green)' : 'var(--red)',
                    padding: 2, display: 'flex', alignItems: 'center',
                    transition: 'all 0.15s',
                  }}
                >
                  {activeTab === 'testing' ? <Check size={14} /> : <X size={14} />}
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Clear all button */}
        {tabItems.length > 1 && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
            <button
              onClick={() => clearAll(activeTab)}
              style={{
                display: 'flex', alignItems: 'center', gap: 4,
                padding: '6px 10px',
                background: 'none',
                border: '1px solid var(--gray-300)',
                borderRadius: 6,
                fontSize: 11,
                color: 'var(--gray-700)',
                cursor: 'pointer',
                fontFamily: 'inherit',
                transition: 'all 0.15s',
              }}
            >
              <Trash2 size={11} /> Clear All
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
